import {
  COLOR_SPACE_GLSL,
  WHITE_BALANCE_GLSL,
  SHADOWS_HIGHLIGHTS_GLSL,
  VIBRANCE_GLSL,
  COLOR_SPACE_WGSL,
  WHITE_BALANCE_WGSL,
  SHADOWS_HIGHLIGHTS_WGSL,
  VIBRANCE_WGSL
} from "./index-6jkps947.mjs";
import {
  assertOptionalFiniteNumber,
  validateUnitInterval,
  validateNonNegative,
  validateSignedUnitInterval
} from "./index-wxhvgv2s.mjs";
import {
  WEBGPU_SINGLE_PASS_VERTEX,
  setupWebGpuSinglePass,
  cleanupWebGpuSinglePass,
  applyWebGpuSinglePass
} from "./index-tvfexb7s.mjs";
import {
  assertEffectParamsObject
} from "./index-4fvp93ps.mjs";

// src/color-correction.ts
import { Internals } from "remotion";
var { createEffect, createWebGL2ContextError } = Internals;
var DEFAULT_EXPOSURE = 0;
var DEFAULT_CONTRAST = 1;
var DEFAULT_PIVOT = 0.5;
var DEFAULT_SHADOWS = 0;
var DEFAULT_HIGHLIGHTS = 0;
var DEFAULT_WHITES = 0;
var DEFAULT_BLACKS = 0;
var DEFAULT_TEMPERATURE = 0;
var DEFAULT_TINT = 0;
var DEFAULT_SATURATION = 1;
var DEFAULT_VIBRANCE = 0;
var MIN_EXPOSURE = -5;
var MAX_EXPOSURE = 5;
var signedAdjustmentSchema = (description) => ({
  type: "number",
  min: -1,
  max: 1,
  step: 0.01,
  default: 0,
  description,
  hiddenFromList: false
});
var colorCorrectionSchema = {
  exposure: {
    type: "number",
    min: MIN_EXPOSURE,
    max: MAX_EXPOSURE,
    step: 0.1,
    default: DEFAULT_EXPOSURE,
    description: "Exposure",
    hiddenFromList: false
  },
  contrast: {
    type: "number",
    min: 0,
    max: 3,
    step: 0.01,
    default: DEFAULT_CONTRAST,
    description: "Contrast",
    hiddenFromList: false
  },
  pivot: {
    type: "number",
    min: 0,
    max: 1,
    step: 0.01,
    default: DEFAULT_PIVOT,
    description: "Contrast pivot",
    hiddenFromList: false
  },
  shadows: signedAdjustmentSchema("Shadows"),
  highlights: signedAdjustmentSchema("Highlights"),
  whites: signedAdjustmentSchema("Whites"),
  blacks: signedAdjustmentSchema("Blacks"),
  temperature: signedAdjustmentSchema("Temperature"),
  tint: signedAdjustmentSchema("Tint"),
  saturation: {
    type: "number",
    min: 0,
    max: 3,
    step: 0.01,
    default: DEFAULT_SATURATION,
    description: "Saturation",
    hiddenFromList: false
  },
  vibrance: signedAdjustmentSchema("Vibrance")
};
var resolve = (params) => ({
  exposure: params.exposure ?? DEFAULT_EXPOSURE,
  contrast: params.contrast ?? DEFAULT_CONTRAST,
  pivot: params.pivot ?? DEFAULT_PIVOT,
  shadows: params.shadows ?? DEFAULT_SHADOWS,
  highlights: params.highlights ?? DEFAULT_HIGHLIGHTS,
  whites: params.whites ?? DEFAULT_WHITES,
  blacks: params.blacks ?? DEFAULT_BLACKS,
  temperature: params.temperature ?? DEFAULT_TEMPERATURE,
  tint: params.tint ?? DEFAULT_TINT,
  saturation: params.saturation ?? DEFAULT_SATURATION,
  vibrance: params.vibrance ?? DEFAULT_VIBRANCE
});
var validateColorCorrectionParams = (params) => {
  assertEffectParamsObject(params, "Color correction");
  assertOptionalFiniteNumber(params.exposure, "exposure");
  assertOptionalFiniteNumber(params.contrast, "contrast");
  assertOptionalFiniteNumber(params.pivot, "pivot");
  assertOptionalFiniteNumber(params.shadows, "shadows");
  assertOptionalFiniteNumber(params.highlights, "highlights");
  assertOptionalFiniteNumber(params.whites, "whites");
  assertOptionalFiniteNumber(params.blacks, "blacks");
  assertOptionalFiniteNumber(params.temperature, "temperature");
  assertOptionalFiniteNumber(params.tint, "tint");
  assertOptionalFiniteNumber(params.saturation, "saturation");
  assertOptionalFiniteNumber(params.vibrance, "vibrance");
  const resolved = resolve(params);
  if (resolved.exposure < MIN_EXPOSURE) {
    throw new TypeError(`"exposure" must be >= ${MIN_EXPOSURE}, but got ${JSON.stringify(resolved.exposure)}`);
  }
  if (resolved.exposure > MAX_EXPOSURE) {
    throw new TypeError(`"exposure" must be <= ${MAX_EXPOSURE}, but got ${JSON.stringify(resolved.exposure)}`);
  }
  validateNonNegative(resolved.contrast, "contrast");
  validateUnitInterval(resolved.pivot, "pivot");
  validateSignedUnitInterval(resolved.shadows, "shadows");
  validateSignedUnitInterval(resolved.highlights, "highlights");
  validateSignedUnitInterval(resolved.whites, "whites");
  validateSignedUnitInterval(resolved.blacks, "blacks");
  validateSignedUnitInterval(resolved.temperature, "temperature");
  validateSignedUnitInterval(resolved.tint, "tint");
  validateNonNegative(resolved.saturation, "saturation");
  validateSignedUnitInterval(resolved.vibrance, "vibrance");
};
var VERTEX_SHADER = `#version 300 es
in vec2 aPos;
in vec2 aUv;
out vec2 vUv;

void main() {
	vUv = aUv;
	gl_Position = vec4(aPos, 0.0, 1.0);
}
`;
var FRAGMENT_SHADER = `#version 300 es
precision highp float;

in vec2 vUv;
out vec4 fragColor;

uniform sampler2D uSource;
uniform float uExposure;
uniform float uContrast;
uniform float uPivot;
uniform float uShadows;
uniform float uHighlights;
uniform float uWhites;
uniform float uBlacks;
uniform float uTemperature;
uniform float uTint;
uniform float uSaturation;
uniform float uVibrance;

${COLOR_SPACE_GLSL}
${WHITE_BALANCE_GLSL}
${SHADOWS_HIGHLIGHTS_GLSL}
${VIBRANCE_GLSL}

vec3 applyEndpointTones(vec3 color, float blacks, float whites) {
	float luminance = dot(color, vec3(0.2126, 0.7152, 0.0722));
	float blackWeight = 1.0 - smoothstep(0.0, 0.35, luminance);
	float whiteWeight = smoothstep(0.65, 1.0, luminance);
	blackWeight *= blackWeight;
	whiteWeight *= whiteWeight;

	// Moving tones toward their named endpoint uses the full adjustment.
	// Moving them away is damped to avoid flattening the image too aggressively.
	if (blacks >= 0.0) {
		color += (vec3(1.0) - color) * blacks * blackWeight * 0.2;
	} else {
		color *= 1.0 + blacks * blackWeight;
	}

	if (whites >= 0.0) {
		color += (vec3(1.0) - color) * whites * whiteWeight;
	} else {
		color *= 1.0 + whites * whiteWeight * 0.2;
	}

	return clamp(color, 0.0, 1.0);
}

void main() {
	vec4 sourceColor = texture(uSource, vUv);
	float alpha = sourceColor.a;

	if (alpha <= 0.0) {
		fragColor = vec4(0.0);
		return;
	}

	if (
		uExposure == 0.0 &&
		uContrast == 1.0 &&
		uPivot == 0.5 &&
		uShadows == 0.0 &&
		uHighlights == 0.0 &&
		uWhites == 0.0 &&
		uBlacks == 0.0 &&
		uTemperature == 0.0 &&
		uTint == 0.0 &&
		uSaturation == 1.0 &&
		uVibrance == 0.0
	) {
		fragColor = sourceColor;
		return;
	}

	vec3 color = sourceColor.rgb / alpha;
	vec3 linear = srgbToLinear(color) * exp2(uExposure);
	linear = applyWhiteBalanceLinear(linear, uTemperature, uTint);
	color = linearToSrgb(linear);

	float tonalStops = getShadowsHighlightsStops(
		color,
		uShadows,
		uHighlights
	);
	linear = srgbToLinear(color) * exp2(tonalStops);
	color = linearToSrgb(linear);
	color = applyEndpointTones(color, uBlacks, uWhites);
	color = clamp((color - vec3(uPivot)) * uContrast + vec3(uPivot), 0.0, 1.0);

	float luminance = dot(color, vec3(0.213, 0.715, 0.072));
	color = clamp(
		vec3(luminance) + (color - vec3(luminance)) * uSaturation,
		0.0,
		1.0
	);
	color = applyVibrance(color, uVibrance);

	fragColor = vec4(color * alpha, alpha);
}
`;
var WEBGPU_SHADER = `
${WEBGPU_SINGLE_PASS_VERTEX}
struct Params {
	exposure: f32,
	contrast: f32,
	pivot: f32,
	shadows: f32,
	highlights: f32,
	whites: f32,
	blacks: f32,
	temperature: f32,
	tint: f32,
	saturation: f32,
	vibrance: f32,
	padding0: f32,
}
@group(0) @binding(0) var source: texture_2d<f32>;
@group(0) @binding(1) var sourceSampler: sampler;
@group(0) @binding(2) var<uniform> params: Params;
${COLOR_SPACE_WGSL}
${WHITE_BALANCE_WGSL}
${SHADOWS_HIGHLIGHTS_WGSL}
${VIBRANCE_WGSL}

fn applyEndpointTones(inputColor: vec3f, blacks: f32, whites: f32) -> vec3f {
	var color = inputColor;
	let luminance = dot(color, vec3f(0.2126, 0.7152, 0.0722));
	var blackWeight = 1.0 - smoothstep(0.0, 0.35, luminance);
	var whiteWeight = smoothstep(0.65, 1.0, luminance);
	blackWeight *= blackWeight;
	whiteWeight *= whiteWeight;
	if (blacks >= 0.0) {
		color += (vec3f(1.0) - color) * blacks * blackWeight * 0.2;
	} else {
		color *= 1.0 + blacks * blackWeight;
	}
	if (whites >= 0.0) {
		color += (vec3f(1.0) - color) * whites * whiteWeight;
	} else {
		color *= 1.0 + whites * whiteWeight * 0.2;
	}
	return clamp(color, vec3f(0.0), vec3f(1.0));
}

@fragment fn fragment(input: VertexOutput) -> @location(0) vec4f {
	let sourceColor = textureSampleLevel(source, sourceSampler, input.uv, 0.0);
	let alpha = sourceColor.a;
	if (alpha <= 0.0) {
		return vec4f(0.0);
	}
	if (
		params.exposure == 0.0 && params.contrast == 1.0 && params.pivot == 0.5 &&
		params.shadows == 0.0 && params.highlights == 0.0 && params.whites == 0.0 &&
		params.blacks == 0.0 && params.temperature == 0.0 && params.tint == 0.0 &&
		params.saturation == 1.0 && params.vibrance == 0.0
	) {
		return sourceColor;
	}
	var color = sourceColor.rgb / alpha;
	var linear = srgbToLinear(color) * exp2(params.exposure);
	linear = applyWhiteBalanceLinear(linear, params.temperature, params.tint);
	color = linearToSrgb(linear);
	let tonalStops = getShadowsHighlightsStops(color, params.shadows, params.highlights);
	linear = srgbToLinear(color) * exp2(tonalStops);
	color = linearToSrgb(linear);
	color = applyEndpointTones(color, params.blacks, params.whites);
	color = clamp((color - vec3f(params.pivot)) * params.contrast + vec3f(params.pivot), vec3f(0.0), vec3f(1.0));
	let luminance = dot(color, vec3f(0.213, 0.715, 0.072));
	color = clamp(vec3f(luminance) + (color - vec3f(luminance)) * params.saturation, vec3f(0.0), vec3f(1.0));
	color = applyVibrance(color, params.vibrance);
	return vec4f(color * alpha, alpha);
}
`;
var compileShader = (gl, type, source) => {
  const shader = gl.createShader(type);
  if (!shader) {
    throw new Error("Failed to create color correction shader");
  }
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Color correction shader compile failed: ${log ?? "(no log)"}`);
  }
  return shader;
};
var createProgram = (gl) => {
  const vertexShader = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
  const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
  const program = gl.createProgram();
  if (!program) {
    throw new Error("Failed to create color correction shader program");
  }
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(`Color correction shader link failed: ${log ?? "(no log)"}`);
  }
  return program;
};
var createTexture = (gl) => {
  const texture = gl.createTexture();
  if (!texture) {
    throw new Error("Failed to create color correction texture");
  }
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return texture;
};
var setupColorCorrection = (target) => {
  const gl = target.getContext("webgl2", {
    premultipliedAlpha: true,
    alpha: true,
    preserveDrawingBuffer: true
  });
  if (!gl) {
    throw createWebGL2ContextError("color correction effect");
  }
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
  const program = createProgram(gl);
  const vao = gl.createVertexArray();
  if (!vao) {
    throw new Error("Failed to create color correction vertex array");
  }
  const vbo = gl.createBuffer();
  if (!vbo) {
    throw new Error("Failed to create color correction vertex buffer");
  }
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 0, 0, 1, -1, 1, 0, -1, 1, 0, 1, 1, 1, 1, 1]), gl.STATIC_DRAW);
  const aPos = gl.getAttribLocation(program, "aPos");
  const aUv = gl.getAttribLocation(program, "aUv");
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 16, 0);
  gl.enableVertexAttribArray(aUv);
  gl.vertexAttribPointer(aUv, 2, gl.FLOAT, false, 16, 8);
  gl.bindVertexArray(null);
  return {
    gl,
    program,
    vao,
    vbo,
    textureSource: createTexture(gl),
    uniforms: {
      uSource: gl.getUniformLocation(program, "uSource"),
      uExposure: gl.getUniformLocation(program, "uExposure"),
      uContrast: gl.getUniformLocation(program, "uContrast"),
      uPivot: gl.getUniformLocation(program, "uPivot"),
      uShadows: gl.getUniformLocation(program, "uShadows"),
      uHighlights: gl.getUniformLocation(program, "uHighlights"),
      uWhites: gl.getUniformLocation(program, "uWhites"),
      uBlacks: gl.getUniformLocation(program, "uBlacks"),
      uTemperature: gl.getUniformLocation(program, "uTemperature"),
      uTint: gl.getUniformLocation(program, "uTint"),
      uSaturation: gl.getUniformLocation(program, "uSaturation"),
      uVibrance: gl.getUniformLocation(program, "uVibrance")
    }
  };
};
var colorCorrection = createEffect({
  type: "remotion/color-correction",
  label: "colorCorrection()",
  documentationLink: "https://www.remotion.dev/docs/effects/color-correction",
  calculateKey: (params) => {
    const r = resolve(params);
    return `color-correction-${r.exposure}-${r.contrast}-${r.pivot}-${r.shadows}-${r.highlights}-${r.whites}-${r.blacks}-${r.temperature}-${r.tint}-${r.saturation}-${r.vibrance}`;
  },
  implementations: {
    webgpu: Internals.createEffectImplementation({
      setup: (target) => setupWebGpuSinglePass(target, "color correction"),
      apply: ({ source, width, height, params, state, gpuDevice }) => {
        if (!gpuDevice) {
          throw new Error("A WebGPU device is required for the color correction effect");
        }
        const r = resolve(params);
        applyWebGpuSinglePass({
          state,
          source,
          width,
          height,
          device: gpuDevice,
          shader: WEBGPU_SHADER,
          uniformData: new Float32Array([
            r.exposure,
            r.contrast,
            r.pivot,
            r.shadows,
            r.highlights,
            r.whites,
            r.blacks,
            r.temperature,
            r.tint,
            r.saturation,
            r.vibrance,
            0
          ])
        });
      },
      cleanup: cleanupWebGpuSinglePass
    }),
    webgl2: Internals.createEffectImplementation({
      setup: setupColorCorrection,
      apply: ({ source, width, height, params, state, flipSourceY }) => {
        const r = resolve(params);
        const { gl, program, textureSource, uniforms, vao } = state;
        gl.viewport(0, 0, width, height);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, textureSource);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, flipSourceY);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
        gl.useProgram(program);
        if (uniforms.uSource)
          gl.uniform1i(uniforms.uSource, 0);
        if (uniforms.uExposure)
          gl.uniform1f(uniforms.uExposure, r.exposure);
        if (uniforms.uContrast)
          gl.uniform1f(uniforms.uContrast, r.contrast);
        if (uniforms.uPivot)
          gl.uniform1f(uniforms.uPivot, r.pivot);
        if (uniforms.uShadows)
          gl.uniform1f(uniforms.uShadows, r.shadows);
        if (uniforms.uHighlights) {
          gl.uniform1f(uniforms.uHighlights, r.highlights);
        }
        if (uniforms.uWhites)
          gl.uniform1f(uniforms.uWhites, r.whites);
        if (uniforms.uBlacks)
          gl.uniform1f(uniforms.uBlacks, r.blacks);
        if (uniforms.uTemperature) {
          gl.uniform1f(uniforms.uTemperature, r.temperature);
        }
        if (uniforms.uTint)
          gl.uniform1f(uniforms.uTint, r.tint);
        if (uniforms.uSaturation) {
          gl.uniform1f(uniforms.uSaturation, r.saturation);
        }
        if (uniforms.uVibrance)
          gl.uniform1f(uniforms.uVibrance, r.vibrance);
        gl.bindVertexArray(vao);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        gl.bindVertexArray(null);
        gl.bindTexture(gl.TEXTURE_2D, null);
        gl.useProgram(null);
      },
      cleanup: ({ gl, program, vao, vbo, textureSource }) => {
        gl.deleteTexture(textureSource);
        gl.deleteBuffer(vbo);
        gl.deleteProgram(program);
        gl.deleteVertexArray(vao);
      }
    })
  },
  schema: colorCorrectionSchema,
  validateParams: validateColorCorrectionParams
});
export {
  colorCorrection
};
